import { useState, useEffect, useRef } from 'react';
import HTMLParser from 'html-react-parser';
import {
  ConsentTypeContentResponseModel,
  NotificationPreferenceDto,
  NotificationTypeContentResponseModel,
  UpdateNotificationPreferenceRequest,
} from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { InformativeSheet } from '@/components/shared/sheets/informative';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';

// ─── Template resolver ───────────────────────────────────────────────────────

function resolveNotificationTemplate(
  template: string | undefined,
  type: NotificationTypeContentResponseModel
): string {
  if (!template) return '';

  let result = template.replace(/\{\{notificationType\.name\}\}/g, type.name ?? '');

  if (result.includes('{{notificationType.consentDependencies}}')) {
    const deps = (type.properties?.consentsDependencies ?? []) as ConsentTypeContentResponseModel[];
    const listHtml =
      deps.length > 0
        ? `<ul style="list-style:none;padding:12px 0;margin:0;display:flex;flex-direction:column;gap:8px;">${deps
            .map(dep => {
              const label = dep.properties?.label ?? dep.name ?? '';
              const description = dep.properties?.description?.markup ?? '';
              return `<li style="display:flex;gap:8px;align-items:flex-start;">
                <span style="display:flex;flex-direction:column;gap:4px;min-width:0;">
                  <span style="font-weight:bold;font-family:'PP Neue Montreal',sans-serif;font-size:14px;color:#1a1a1a;letter-spacing:0.5px;line-height:normal;">${label}</span>
                  ${description ? `<span style="font-family:'PP Neue Montreal',sans-serif;font-size:12px;color:#848484;letter-spacing:0.5px;line-height:normal;">${description}</span>` : ''}
                </span>
              </li>`;
            })
            .join('')}</ul>`
        : '';
    result = result.replace(/\{\{notificationType\.consentDependencies\}\}/g, listHtml);
  }

  return result;
}

// ─── ProfileSettingsNotifications ────────────────────────────────────────────

export interface CookiesModalProps {
  title?: string;
  descriptionText?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export interface ProfileSettingsNotificationsProps {
  /** Intro paragraph shown above the toggle list. Falls back to translation when absent. */
  subTitle?: string;
  /** Fully-hydrated notification type CMS items. */
  notificationTypes: NotificationTypeContentResponseModel[];
  /** Current user preferences used to seed the toggle state. */
  preferences: NotificationPreferenceDto[];
  /** Whether the initial data fetch is in progress. */
  loading?: boolean;
  /** Whether a save request is in progress; disables the save button. */
  saving?: boolean;
  /** CMS label overrides for the footer buttons. */
  saveChoicesLabel?: string;
  privacyPolicyLabel?: string;
  /** Content for the consent-required sheet shown when toggling a type with consent dependencies. */
  cookiesModal?: CookiesModalProps;
  /** Called with the full updated preferences array when the user taps Save. */
  onSave: (notifications: UpdateNotificationPreferenceRequest[]) => Promise<void>;
  /**
   * Called when the user confirms the consent sheet for a notification type that has
   * consent dependencies. Receives the type so the caller can accept those consents.
   */
  onConsentDependenciesConfirmed?: (type: NotificationTypeContentResponseModel) => void;
  /** Called when the user taps the Privacy Policy link. */
  onPrivacyPolicy?: () => void;
}

export function ProfileSettingsNotifications({
  subTitle,
  notificationTypes,
  preferences,
  loading = false,
  saving = false,
  saveChoicesLabel,
  privacyPolicyLabel,
  cookiesModal,
  onSave,
  onConsentDependenciesConfirmed,
  onPrivacyPolicy,
}: ProfileSettingsNotificationsProps) {
  const t = useClientTranslation();
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [pendingConsentType, setPendingConsentType] = useState<NotificationTypeContentResponseModel | null>(
    null
  );
  const lastConsentTypeRef = useRef<NotificationTypeContentResponseModel | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const initial: Record<string, boolean> = {};
    for (const pref of preferences) {
      if (pref.notificationTypeNodeKey) {
        initial[pref.notificationTypeNodeKey] = pref.isEnabled ?? false;
      }
    }
    setDraft(initial);
  }, [preferences]);

  function handleCheckedChange(type: NotificationTypeContentResponseModel, val: boolean) {
    setDraft(prev => ({ ...prev, [type.id]: val }));
    if (val && (type.properties?.consentsDependencies?.length ?? 0) > 0) {
      lastConsentTypeRef.current = type;
      setPendingConsentType(type);
    }
  }

  function handleConsentConfirm() {
    if (pendingConsentType) {
      onConsentDependenciesConfirmed?.(pendingConsentType);
    }
    setPendingConsentType(null);
  }

  async function handleSave() {
    const payload = notificationTypes.map(type => ({
      notificationTypeNodeKey: type.id,
      isEnabled: draft[type.id] ?? false,
    }));
    await onSave(payload);
  }

  return (
    <div className="pt-safe-area pb-safe-area mb-40 flex h-full flex-col">
      {/* Scrollable content */}
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 pt-4 pb-6">
        {loading ? (
          <SpinLoading wrapperClassName="min-h-40" />
        ) : (
          <>
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
              {subTitle ?? t('App.Labels.NotificationsSubTitle')}
            </p>

            <div className="flex flex-col">
              {notificationTypes.map(type => {
                const description = type.properties?.description?.markup;
                return (
                  <div key={type.id} className="border-jncquoi-neutral-200 border-b-[0.5px] py-4">
                    <Switch
                      checked={draft[type.id] ?? false}
                      onCheckedChange={val => handleCheckedChange(type, val)}
                      labelClassName="font-pp-neue-montreal text-jncquoi-neutral-1000"
                      label={type.name ?? ''}
                      description={description ? HTMLParser(description) : undefined}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {!Capacitor.isNativePlatform() && (
        <button
          type="button"
          onClick={() => navigate('/notifications/inbox')}
          className="text-muted-foreground hidden px-4 py-2 text-left text-sm underline"
        >
          {t('App.Labels.ViewNotificationHistory')}
        </button>
      )}

      {/* Footer */}
      <div className="border-jncquoi-neutral-400 fixed inset-x-0 bottom-0 flex flex-col gap-2 border-t-[0.5px] bg-white px-4 pt-6 pb-10.5">
        <Button
          variant="primary-solid"
          palette="dark"
          size="md"
          loading={saving}
          disabled={saving}
          onClick={handleSave}
          className="w-full"
        >
          {saveChoicesLabel ?? t('App.Labels.SaveChoices')}
        </Button>
        <Button variant="tertiary-link" palette="dark" size="sm" onClick={onPrivacyPolicy} className="w-full">
          {privacyPolicyLabel ?? t('App.Labels.SeeOurPrivacyPolicy')}
        </Button>
      </div>

      <InformativeSheet
        open={pendingConsentType !== null}
        onOpenChange={open => {
          if (!open) setPendingConsentType(null);
        }}
        title={
          (pendingConsentType ?? lastConsentTypeRef.current)
            ? resolveNotificationTemplate(
                cookiesModal?.title,
                (pendingConsentType ?? lastConsentTypeRef.current)!
              )
            : cookiesModal?.title
        }
        descriptionText={
          (pendingConsentType ?? lastConsentTypeRef.current)
            ? resolveNotificationTemplate(
                cookiesModal?.descriptionText,
                (pendingConsentType ?? lastConsentTypeRef.current)!
              )
            : cookiesModal?.descriptionText
        }
        confirmLabel={cookiesModal?.confirmLabel}
        cancelLabel={cookiesModal?.cancelLabel}
        onConfirm={handleConsentConfirm}
      />
    </div>
  );
}
